import React, { useState, useEffect, useCallback } from 'react';
import { Shield, Search, ChevronLeft, ChevronRight, Clock, User, FileText, Download, CheckCircle, XCircle, LogIn, List, Activity } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/utils/api';
import { AnalyticsSkeleton } from '@/components/LoadingIndicators';
import AuditTimeline from '@/components/AuditTimeline';

const ACTION_CONFIG = {
  login: { label: 'Login', icon: LogIn, color: 'bg-blue-100 text-blue-700' },
  proposal_submit: { label: 'Proposal Submitted', icon: FileText, color: 'bg-amber-100 text-amber-700' },
  proposal_approve: { label: 'Proposal Approved', icon: CheckCircle, color: 'bg-emerald-100 text-emerald-700' },
  proposal_reject: { label: 'Proposal Rejected', icon: XCircle, color: 'bg-red-100 text-red-700' },
  export: { label: 'Export', icon: Download, color: 'bg-indigo-100 text-indigo-700' },
  calculation: { label: 'Calculation', icon: FileText, color: 'bg-purple-100 text-purple-700' },
  scenario_create: { label: 'Scenario Created', icon: FileText, color: 'bg-cyan-100 text-cyan-700' },
  scenario_delete: { label: 'Scenario Deleted', icon: XCircle, color: 'bg-orange-100 text-orange-700' },
  user_create: { label: 'User Created', icon: User, color: 'bg-teal-100 text-teal-700' },
  user_update: { label: 'User Updated', icon: User, color: 'bg-slate-100 text-slate-700' },
  settings_change: { label: 'Settings Changed', icon: FileText, color: 'bg-gray-100 text-gray-700' },
};

function ActionBadge({ action }) {
  const config = ACTION_CONFIG[action] || { label: action, icon: FileText, color: 'bg-slate-100 text-slate-700' };
  const Icon = config.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`} data-testid={`badge-${action}`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
}

function formatTimestamp(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'timeline'

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, limit: 30 };
      if (search) params.search = search;
      if (actionFilter) params.action = actionFilter;
      const res = await api.get('/audit/logs', { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
    } catch (err) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, search, actionFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await api.get('/audit/logs/summary');
      setSummary(res.data);
    } catch {}
  }, []);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchLogs();
  };

  return (
    <div className="min-h-screen bg-slate-50 p-6 lg:p-8" data-testid="audit-log-page">
      {/* Header */}
      <div className="mb-6 max-w-[1400px]">
        <h1 className="text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2.5">
          <Shield className="w-5 h-5 text-sky-600" />
          Audit Log
        </h1>
        <p className="text-sm text-slate-400 mt-0.5">Track user actions and system events</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="glass-card p-4">
            <p className="text-sm text-slate-500">Total Events</p>
            <p className="text-2xl font-bold text-slate-900" data-testid="total-events">{summary.total_entries.toLocaleString()}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-slate-500">Today's Activity</p>
            <p className="text-2xl font-bold text-indigo-600" data-testid="today-events">{summary.today_entries}</p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-slate-500">Top Action</p>
            <p className="text-lg font-semibold text-slate-900">
              {summary.by_action?.[0] ? <ActionBadge action={summary.by_action[0].action} /> : 'N/A'}
            </p>
          </div>
          <div className="glass-card p-4">
            <p className="text-sm text-slate-500">Active Users</p>
            <p className="text-2xl font-bold text-emerald-600">{summary.recent_users?.length || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="glass-card p-4 mb-6">
        <div className="flex flex-wrap gap-4 items-end">
          <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by user, email, or description..."
                className="input-field pl-10 w-full"
                data-testid="audit-search"
              />
            </div>
          </form>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Action Type</label>
            <select
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
              className="input-field"
              data-testid="audit-action-filter"
            >
              <option value="">All Actions</option>
              {Object.entries(ACTION_CONFIG).map(([key, cfg]) => (
                <option key={key} value={key}>{cfg.label}</option>
              ))}
            </select>
          </div>
          
          {/* View Mode Toggle */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">View</label>
            <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'table' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Table View"
              >
                <List className="w-4 h-4" />
                Table
              </button>
              <button
                type="button"
                onClick={() => setViewMode('timeline')}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'timeline' 
                    ? 'bg-white text-slate-900 shadow-sm' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Timeline View"
              >
                <Activity className="w-4 h-4" />
                Timeline
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logs Display */}
      {loading ? (
        <div className="glass-card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2" />
          <p className="text-slate-500">Loading audit logs...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="glass-card p-12 text-center">
          <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg font-medium">No audit logs found</p>
          <p className="text-slate-500 text-sm">Actions will appear here as users interact with the system</p>
        </div>
      ) : viewMode === 'timeline' ? (
        <div className="glass-card p-6">
          <AuditTimeline entries={logs} />
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <table className="w-full" data-testid="audit-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Timestamp</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Resource</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log, i) => (
                <tr key={i} className="border-b border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`audit-row-${i}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5 text-sm text-slate-600">
                      <Clock className="w-3.5 h-3.5 text-slate-400" />
                      {formatTimestamp(log.timestamp)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-slate-900">{log.user_name || 'System'}</div>
                    <div className="text-xs text-slate-500">{log.user_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <ActionBadge action={log.action} />
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">
                    {log.resource_type && (
                      <span className="capitalize">{log.resource_type}</span>
                    )}
                    {log.resource_id && (
                      <span className="text-xs text-slate-400 ml-1">#{log.resource_id.slice(0, 8)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600 max-w-xs truncate">
                    {log.details?.description || log.details?.name || log.details?.scenario_name || log.details?.format || log.details?.role || ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              Showing {(page - 1) * 30 + 1}–{Math.min(page * 30, total)} of {total} events
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-50 hover:bg-slate-100 transition-colors"
                data-testid="audit-prev-page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-slate-600 px-2">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-slate-300 disabled:opacity-50 hover:bg-slate-100 transition-colors"
                data-testid="audit-next-page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
